import React, { useState, useRef, useEffect } from 'react';
import {
  IconButton,
  CircularProgress,
  Tooltip,
  Box,
  Typography,
  Keyframes
} from '@mui/material';
import {
  Mic,
  Stop,
  Error as ErrorIcon
} from '@mui/icons-material';
import { chatAPI } from '../../services/api';

const MAX_RECORDING_TIME = 180; // 3 minutes

const CompactVoiceRecorder = ({ onTranscriptionComplete, onError, colorScheme = 'primary' }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    return () => {
      stopMediaTracks();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const stopMediaTracks = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const startRecording = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { echoCancellation: true, noiseSuppression: true }
      });
      
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm;codecs=opus' });
        await handleTranscribe(audioBlob);
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= MAX_RECORDING_TIME) {
            stopRecording();
            return MAX_RECORDING_TIME;
          }
          return prev + 1;
        });
      }, 1000);

    } catch (err) {
      console.error('Mic access error:', err);
      setError('Mic access failed');
      onError?.('Failed to start recording');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      stopMediaTracks();
    }
  };

  const handleTranscribe = async (blob) => {
    setIsTranscribing(true);
    try {
      const result = await chatAPI.transcribeAudio(blob);
      if (result.success && result.transcription) {
        onTranscriptionComplete?.(result.transcription);
      } else {
        throw new Error(result.message || 'Transcription failed');
      }
    } catch (err) {
      setError('Transcription failed');
      onError?.('Transcription failed');
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  if (isTranscribing) {
    return <CircularProgress size={24} sx={{ m: 1 }} />;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {isRecording && (
        <Typography variant="caption" sx={{ mr: 1, color: 'error.main', fontWeight: 'bold' }}>
          {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
        </Typography>
      )}
      <Tooltip title={isRecording ? "Stop Recording" : (error ? error : "Voice Input")}>
        <IconButton 
          onClick={handleClick} 
          color={isRecording ? "error" : "primary"}
          size="small"
          sx={{
            p: 1,
            bgcolor: isRecording ? 'rgba(211, 47, 47, 0.1)' : 'rgba(25, 118, 210, 0.05)',
            border: '1px solid',
            borderColor: isRecording ? 'error.main' : 'primary.light',
            '&:hover': {
              bgcolor: isRecording ? 'rgba(211, 47, 47, 0.2)' : 'rgba(25, 118, 210, 0.12)',
            },
            animation: isRecording ? 'pulse 1.2s infinite' : 'none',
            '@keyframes pulse': {
              '0%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.4)' },
              '70%': { transform: 'scale(1.15)', boxShadow: '0 0 0 10px rgba(211, 47, 47, 0)' },
              '100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)' },
            }
          }}
        >
          {error ? <ErrorIcon color="error" /> : (isRecording ? <Stop sx={{ fontSize: 24 }} /> : <Mic sx={{ fontSize: 24 }} />)}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default CompactVoiceRecorder;
